import { AccessTime, Star, StarBorder } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";

/**
 * Stable class names (not emotion-generated) so a row's wrapper can reveal
 * this button on hover/focus without depending on ancestor-tag CSS selectors
 * like `li:hover &`, and without any JS-tracked hover state re-rendering the
 * list on every mouse move — plain `:hover` is instant.
 */
export const RESOURCE_STAR_BUTTON_CLASS = "resource-star-button";
export const RESOURCE_STAR_BUTTON_VISIBLE_CLASS =
  "resource-star-button-visible";
export const RESOURCE_STAR_ICON_CLASS = "resource-star-icon";
export const RESOURCE_CLOCK_ICON_CLASS = "resource-clock-icon";

/**
 * Reveals the star button (and swaps its star/clock icon layers) when a row
 * or option wrapper is hovered/focused. Spread into the wrapper's own
 * style/sx object — the wrapper is the one with `:hover`/`:focus-within`, not
 * this button itself.
 */
export const RESOURCE_STAR_HOVER_STYLES = {
  [`&:hover .${RESOURCE_STAR_BUTTON_CLASS}, &:focus-within .${RESOURCE_STAR_BUTTON_CLASS}`]:
    { opacity: 1 },
  [`&:hover .${RESOURCE_STAR_BUTTON_CLASS}.${RESOURCE_STAR_BUTTON_VISIBLE_CLASS}, &:focus-within .${RESOURCE_STAR_BUTTON_CLASS}.${RESOURCE_STAR_BUTTON_VISIBLE_CLASS}`]:
    { opacity: 1 },
  [`&:hover .${RESOURCE_STAR_BUTTON_CLASS} .${RESOURCE_STAR_ICON_CLASS}, &:focus-within .${RESOURCE_STAR_BUTTON_CLASS} .${RESOURCE_STAR_ICON_CLASS}`]:
    { opacity: 1 },
  [`&:hover .${RESOURCE_STAR_BUTTON_CLASS} .${RESOURCE_CLOCK_ICON_CLASS}, &:focus-within .${RESOURCE_STAR_BUTTON_CLASS} .${RESOURCE_CLOCK_ICON_CLASS}`]:
    { opacity: 0 },
} as const;

const StyledIconButton = styled(IconButton)({
  padding: 2,
  marginLeft: "auto",
  flexShrink: 0,
  position: "relative",
  opacity: 0,
  [`&.${RESOURCE_STAR_BUTTON_VISIBLE_CLASS}`]: {
    opacity: 1,
  },
});

const IconWrapper = styled("span")({
  position: "relative",
  display: "inline-flex",
  width: 24,
  height: 24,
  alignItems: "center",
  justifyContent: "center",
});

const IconLayer = styled("span")({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "opacity 0.15s ease-in-out",
});

const StarIconLayer = styled(IconLayer)({
  opacity: 0,
  [`&[data-visible="true"]`]: {
    opacity: 1,
  },
});

const ClockIconLayer = styled(IconLayer)({
  opacity: 1,
});

export interface ResourceStarButtonProps {
  starred: boolean;
  /** When true and not starred, a clock icon is shown until hover reveals the star. */
  recent?: boolean;
  onToggle: () => void;
}

/**
 * Hover-reveal star toggle for a resource picker option row.
 *
 * - Starred items always show a filled yellow star.
 * - Recent (but not starred) items show a clock icon by default; hovering the
 *   row reveals a hollow star so the user can add a star.
 * - Other items show nothing until the row is hovered/focused.
 *
 * Must be rendered inside a row wrapper that reveals `.resource-star-button`
 * and swaps the star/clock icons on `&:hover`/`&:focus-within`.
 */
function ResourceStarButton({
  starred,
  recent = false,
  onToggle,
}: ResourceStarButtonProps) {
  const visible = starred || recent;
  return (
    <StyledIconButton
      className={[
        RESOURCE_STAR_BUTTON_CLASS,
        visible ? RESOURCE_STAR_BUTTON_VISIBLE_CLASS : "",
      ]
        .filter(Boolean)
        .join(" ")}
      size="small"
      aria-label={
        starred
          ? "Remove star"
          : recent
            ? "Add star to recent item"
            : "Add star"
      }
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <IconWrapper>
        {starred ? (
          <StarIconLayer
            className={RESOURCE_STAR_ICON_CLASS}
            data-visible="true"
          >
            <Star fontSize="small" sx={{ color: "warning.main" }} />
          </StarIconLayer>
        ) : (
          <>
            <StarIconLayer className={RESOURCE_STAR_ICON_CLASS}>
              <StarBorder fontSize="small" />
            </StarIconLayer>
            {recent && (
              <ClockIconLayer className={RESOURCE_CLOCK_ICON_CLASS}>
                <AccessTime fontSize="small" color="action" />
              </ClockIconLayer>
            )}
          </>
        )}
      </IconWrapper>
    </StyledIconButton>
  );
}

export default ResourceStarButton;
